import React, { useEffect, useState, useRef, useCallback } from 'react';

import { IBdsTextArea } from '../../../interfaces/bdsInput';
import './BdsTextArea.scss';

const BdsTextArea = ({
  labelText,
  hintText,
  helperText,
  prefixIcon,
  maxLength,
  initialValue,
  maxExpandableLines = 5,
  maxDisplayedLines = 5,
  allowResize = false,
  enabled = true,
  onChanged,
  onSubmitPressed,
  validator,
  inputFormatters
}: IBdsTextArea) => {
  const [value, setValue] = useState<string>(initialValue || '');
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentRows, setCurrentRows] = useState(1);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const updateHeight = useCallback(() => {
    if (textareaRef.current) {
      const newLineCount = value.split('\n').length;
      const visibleRows = Math.min(newLineCount, maxDisplayedLines);
      setCurrentRows(visibleRows);
      if (visibleRows < maxDisplayedLines) {
        if (newLineCount <= maxExpandableLines) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        } else {
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
          textareaRef.current.style.overflowY = 'auto';
        }
      }
    }
  }, [maxDisplayedLines, maxExpandableLines, value]);

  useEffect(() => {
    updateHeight();
  }, [updateHeight, value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    const newLineCount = newValue.split('\n').length;

    if (newLineCount <= maxExpandableLines) {
      if (inputFormatters) {
        inputFormatters.forEach((formatter) => {
          newValue = formatter(newValue);
        });
      }

      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength);
      }

      setValue(newValue);

      if (onChanged) {
        onChanged(newValue);
      }

      if (validator) {
        const validationError = validator(newValue);
        setError(validationError);
      } else {
        setError(null);
      }
    }

    updateHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      const lineCount = value.split('\n').length;

      if (lineCount >= maxExpandableLines) {
        e.preventDefault();
      } else if (onSubmitPressed) {
        onSubmitPressed(value);
      }
    }
  };

  return (
    <div className={`bds-text-area ${error ? 'error' : ''} ${!enabled ? 'disabled' : ''}`}>
      {labelText && (
        <div className={`label-text ${error ? 'error' : ''} ${!enabled ? 'disabled' : ''}`}>{labelText}</div>
      )}
      <button
        className={`row-text-area-container ${isFocused && enabled ? 'focused' : ''} ${
          isHovered && enabled ? 'hovered' : ''
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="text-input-container">
          {prefixIcon && <img className="prefix-icon" src={prefixIcon} alt={`${prefixIcon}`} />}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={hintText}
            disabled={!enabled}
            className="bds-text-area-field"
            onFocus={() => {
              setIsHovered(false);
              setIsFocused(true);
            }}
            style={{
              resize: allowResize ? 'both' : 'none'
            }}
            onBlur={() => setIsFocused(false)}
            maxLength={maxLength}
            rows={currentRows}
          />
          <div className="resize-handle" />
        </div>
      </button>
      {error ? (
        <div className="error-text">{error}</div>
      ) : (
        helperText && <div className="helper-text">{helperText}</div>
      )}
    </div>
  );
};

export default BdsTextArea;
